import React, { useRef, useState, useEffect } from "react";
import { Box, Typography, Card, CardActionArea, CardMedia, useTheme, CardContent } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Favorite } from "@mui/icons-material";
import { StarRating } from "./StarRating";
import type { IRanking, ISavedRecipes } from "../types/recipe";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import { getRankingAverage } from "../utils/helpers/recipeHelper";

interface IThumbnailRowProps {
    recipes: ISavedRecipes[];
    rowTitle: string;
    thumbnailClick: (s: string) => void;
}

const ThumbnailRow: React.FC<IThumbnailRowProps> = ({ rowTitle, thumbnailClick, recipes = [] }) => {
    const theme = useTheme();
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const scrollAmount = window.innerWidth * 0.15;

    const authState = useSelector((state: RootState) => state.auth);

    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollButtons = () => {
        const el = scrollRef.current;
        if (!el) return;

        const { scrollLeft, scrollWidth, clientWidth } = el;

        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    };

    const handleScroll = (direction: "left" | "right") => {
        const el = scrollRef.current;
        if (!el) return;

        const { scrollLeft } = el;
        const delta = direction === "left" ? -scrollAmount : scrollAmount;

        el.scrollTo({
            left: scrollLeft + delta,
            behavior: "smooth",
        });
    };

    // Initialize button visibility & update when content changes
    useEffect(() => {
        updateScrollButtons();
    }, [recipes.length]);

    return (
        <Box sx={{ mb: 2 }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: { xs: 2, sm: 4, md: 6 },
                }}
            >
                <Typography
                    variant="h4"
                    sx={{
                        userSelect: "none",
                        pb: 1,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        borderBottom: "1px solid " + theme.palette.primary.main,
                    }}
                >
                    {rowTitle}
                </Typography>
            </Box>
            <Box sx={{ position: "relative", width: "100%", mt: 2, pt: "6px" }}>
                {/* Left scroll bar */}
                {canScrollLeft && (
                    <Box
                        onClick={() => handleScroll("left")}
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 32,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            background: "linear-gradient(to right, rgba(0,0,0,0.35), transparent)",
                            cursor: "pointer",
                            zIndex: 2,
                        }}
                    >
                        <ChevronLeftIcon sx={{ color: "white", fontSize: 32, ml: 0.5 }} />
                    </Box>
                )}

                {/* Right scroll bar */}
                {canScrollRight && (
                    <Box
                        onClick={() => handleScroll("right")}
                        sx={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: 32,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            background: "linear-gradient(to left, rgba(0,0,0,0.35), transparent)",
                            cursor: "pointer",
                            zIndex: 2,
                        }}
                    >
                        <ChevronRightIcon sx={{ color: "white", fontSize: 32, mr: 0.5 }} />
                    </Box>
                )}

                {/* Thumbnails Row */}
                <Box
                    ref={scrollRef}
                    onScroll={updateScrollButtons}
                    sx={{
                        overflowX: "auto",
                        overflowY: "hidden",
                        px: { xs: 2, sm: 4, md: 6 },
                        scrollBehavior: "smooth",
                        position: "relative",
                        "&::-webkit-scrollbar": {
                            display: "none",
                        },
                        scrollbarWidth: "none",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            pb: 1,
                        }}
                    >
                        {recipes.map((recipe: ISavedRecipes) => {
                            const filteredRanking = recipe.ranking?.filter(
                                (rank: IRanking) => rank.user.username === authState.account?.username
                            );
                            const starRatingValue = filteredRanking ? (filteredRanking[0] ? filteredRanking[0].value : 0) : 0;

                            return (
                                <Card
                                    key={recipe._id}
                                    sx={{
                                        borderRadius: 5,
                                        display: "flex",
                                        flexDirection: "column",
                                        width: "15vw",
                                        flexShrink: 0,
                                        minWidth: 210,
                                        maxWidth: 320,
                                        boxShadow: "0 1px 8px rgba(0,0,0,0.12)",
                                    }}
                                >
                                    <CardActionArea
                                        onClick={() => {
                                            thumbnailClick(recipe._id);
                                        }}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            height: "100%",
                                            alignItems: "stretch",
                                            width: "15vw",
                                            minWidth: 210,
                                            maxWidth: 320,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                position: "absolute",
                                                alignItems: "center",
                                                gap: 0.5,
                                                backgroundColor: theme.palette.background.paper,
                                                border: "2px solid " + theme.palette.primary.main,
                                                borderRadius: 5,
                                                px: 1,
                                                py: 0.5,
                                                right: 5,
                                                top: 220,
                                            }}
                                        >
                                            <Favorite sx={{ fontSize: 24, color: theme.palette.primary.main }} fontSize="small" />
                                            <Typography variant="subtitle2" sx={{ fontSize: 16 }}>
                                                {recipe.likes?.length}
                                            </Typography>
                                        </Box>
                                        <CardMedia
                                            sx={{
                                                borderTopLeftRadius: 20,
                                                borderTopRightRadius: 20,
                                                width: "100%",
                                                minWidth: 210,
                                            }}
                                            component="img"
                                            height="240"
                                            image={recipe.image?.url ?? "/images/recipePlaceholder.png"}
                                            alt={recipe.name}
                                        />
                                        <CardContent
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                flexGrow: 1,
                                                borderBottomLeftRadius: 20,
                                                borderBottomRightRadius: 20,
                                                backgroundColor: theme.palette.background.paper,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    height: 80,
                                                    flexGrow: 1,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        minWidth: 178,
                                                        fontSize: { xs: "16px", sm: "17px", md: "18px" },
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: "vertical",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }}
                                                >
                                                    {recipe.name}
                                                </Typography>
                                                {recipe.user?.username && (
                                                    <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main }}>
                                                        {recipe.user?.username}
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Box
                                                sx={{
                                                    flexGrow: 1,
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "flex-end",
                                                    mt: 1,
                                                }}
                                            >
                                                <StarRating
                                                    size={"sm"}
                                                    objectId={recipe._id}
                                                    average={recipe.ranking ? getRankingAverage(recipe.ranking) : 0}
                                                    count={recipe.ranking?.length}
                                                    value={starRatingValue ?? 0}
                                                />
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ThumbnailRow;
